import { CULTURE_COOKIE_NAME, CULTURE_QUERY_KEY, SUPPORTED_LANGS, DEFAULT_LANG } from '../configs/i18n';

const GOOGLE_TRANSLATE_SCRIPT_ID = 'google-translate-script';
const GOOGLE_TRANSLATE_ELEMENT_ID = 'google_translate_element';
const GOOGTRANS_COOKIE = 'googtrans';

export const getCookie = (name) => {
    if (typeof document === 'undefined') return null;
    const pattern = new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)');
    const match = document.cookie.match(pattern);
    return match ? decodeURIComponent(match[1]) : null;
}

// Extracts a culture's primary language subtag (e.g. "fr-FR" -> "fr") and validates
// it against the languages we support, returning null otherwise.
const langFromCulture = (culture) => {
    if (!culture) return null;
    const lang = culture.split('-')[0].toLowerCase();
    return SUPPORTED_LANGS.includes(lang) ? lang : null;
}

// The "lang" query string param takes priority: it's the freshest signal of what
// the visitor just picked on this exact request. This app's own culture cookie is
// the fallback for requests that don't repeat "?lang=" - e.g. any other page the
// visitor navigates to afterwards. Defaults to DEFAULT_LANG when neither source names
// a supported language.
export const resolveTargetLang = () => {
    if (typeof window !== 'undefined') {
        const fromQuery = langFromCulture(new URLSearchParams(window.location.search).get(CULTURE_QUERY_KEY));
        if (fromQuery) return fromQuery;
    }

    const fromCookie = langFromCulture(getCookie(CULTURE_COOKIE_NAME));
    if (fromCookie) return fromCookie;

    return DEFAULT_LANG;
}

// Domain scopes to also write the googtrans cookie into, besides the plain host-only
// write. Confirmed in production on affiliatequarter.com: the Translate widget itself
// creates its own "googtrans" cookie scoped to ".affiliatequarter.com" (leading dot,
// same host) the first time it initializes, separate from our host-only one - Domain
// is part of a cookie's identity, so our updates to the host-only cookie never touch
// that one, and nothing ever updates it again after that first write. Whichever of
// the two same-named cookies the browser lists first is the one anything doing a
// naive "read the cookie named googtrans" lookup will see (ordering follows path
// length then creation time, not domain) - so the widget can keep rendering whatever
// language was first applied even though our own cookie is updating correctly. We
// also cover one level up in case this app is ever deployed on a subdomain (e.g.
// "affiliateQuarter.com" -> ".frootdigital.com"), mirroring the same
// leading-dot pattern Google's widget could apply there.
const googTransCookieDomains = () => {
    if (typeof window === 'undefined') return [];
    const host = window.location.hostname;
    const parts = host.split('.');
    const domains = [host];
    if (parts.length > 2) domains.push(parts.slice(1).join('.'));
    return domains;
}

// The Google Translate widget hardcodes "googtrans" as its cookie name - unlike our
// own CULTURE_COOKIE_NAME, we can't rename it away to keep it exclusively ours. So we
// write the chosen language into every scope the widget might be reading from (see
// googTransCookieDomains), overwriting each on every selection, so a stale value in
// any of them can't win over the visitor's choice made here.
const setGoogTransCookie = (lang) => {
    const value = `/en/${lang}`;
    document.cookie = `${GOOGTRANS_COOKIE}=${value}; path=/`;
    googTransCookieDomains().forEach((domain) => {
        document.cookie = `${GOOGTRANS_COOKIE}=${value}; path=/; domain=.${domain}`;
    });
}

const CULTURE_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365; // 1 year

// Writes this app's own culture cookie, storing the raw culture (e.g. "fr-FR").
// This app is served as an IIS virtual application on the same domain as other RHP
// sites, so this cookie must stay app-specific (see CULTURE_COOKIE_NAME) - reusing a
// cookie name another app's culture negotiation also writes would let that app
// silently reset the visitor's choice made here on their next navigation.
const setCultureCookie = (culture) => {
    const secure = (typeof window !== 'undefined' && window.location.protocol === 'https:') ? '; Secure' : '';
    document.cookie = `${CULTURE_COOKIE_NAME}=${culture}; path=/; max-age=${CULTURE_COOKIE_MAX_AGE_SECONDS}; SameSite=Strict${secure}`;
}

// Tells Google Translate which language to render. Safe to call before the widget
// script has finished loading - it only sets the cookie the widget reads on init.
export const applyTranslation = (lang) => {
    setGoogTransCookie(lang);
}

// Call when the visitor explicitly picks a language from the dropdown, with the full
// culture from the link they clicked (e.g. "fr-FR"). Persists the choice into this
// app's own culture cookie, plus the Google Translate cookie, so the selection
// survives navigation instead of only lasting for the one request that carried
// "?lang=".
export const selectCulture = (culture) => {
    if (typeof document === 'undefined' || !culture) return;
    setCultureCookie(culture);
    const lang = langFromCulture(culture);
    if (lang) setGoogTransCookie(lang);
}

// Loads the Google Translate widget script and wires up its init callback exactly
// once per page, no matter how many components/effects call this.
export const initGoogleTranslate = () => {
    if (typeof window === 'undefined') return;

    if (!window.googleTranslateElementInit) {
        window.googleTranslateElementInit = function () {
            if (window.__googleTranslateInitialized) return;
            // eslint-disable-next-line no-undef
            new google.translate.TranslateElement(
                { pageLanguage: 'en', autoDisplay: false },
                GOOGLE_TRANSLATE_ELEMENT_ID
            );
            window.__googleTranslateInitialized = true;
        };
    }

    if (document.getElementById(GOOGLE_TRANSLATE_SCRIPT_ID)) return;

    const script = document.createElement('script');
    script.id = GOOGLE_TRANSLATE_SCRIPT_ID;
    script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
}
